import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { organizationService } from "../../../services/api/organization";
import { documentService } from "../../../services/api/documentService";
import { toast } from "sonner";
import Button2 from "../../../components/elements/forms/Button2";
import SlaContent from "./SLAContent";
import StatusModal from "../../../components/Modal/StatusModal";
import PdfPreviewModal from "../../../components/Modal/PdfPreviewModal";
import { format } from "date-fns";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

const SlaPageAgreement: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const id = searchParams.get("Id");
  const slaContentRef = useRef<HTMLDivElement>(null);

  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [clientName, setClientName] = useState("[Client]");
  const [representativeName, setRepresentativeName] = useState(
    "[Representative Name]",
  );
  const [representativeRole, setRepresentativeRole] = useState(
    "[Representative Role]",
  );
  const [agreed, setAgreed] = useState(false);
  const [uploadedSignature, setUploadedSignature] = useState<File | null>(null);
  const [signaturePreview, setSignaturePreview] = useState<string | null>(null);
  const [statusModal, setStatusModal] = useState<{
    isOpen: boolean;
    status: "success" | "error";
    message: string;
  }>({
    isOpen: false,
    status: "success",
    message: "",
  });
  const [previewModal, setPreviewModal] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const [pdfFile, setPdfFile] = useState<File | null>(null);

  useEffect(() => {
    const fetchOrg = async () => {
      if (!id) {
        setLoading(false);
        return;
      }
      try {
        const response = await organizationService.get(id);
        if (response?.data?.name) {
          setClientName(response.data.name);
        }
        if (response?.data?.representativeName) {
          setRepresentativeName(response.data.representativeName);
        }
        if (response?.data?.representativeRole) {
          setRepresentativeRole(response.data.representativeRole);
        }
      } catch (error) {
        console.error("Failed to fetch organization details", error);
        toast.error("Could not load organization details.");
      } finally {
        setLoading(false);
      }
    };
    fetchOrg();
  }, [id]);

  const handleFileUpload = (file: File) => {
    setUploadedSignature(file);
    // Create preview URL
    const previewUrl = URL.createObjectURL(file);
    setSignaturePreview(previewUrl);
  };

  const handleRemoveSignature = () => {
    // Revoke previous preview URL to prevent memory leaks
    if (signaturePreview) {
      URL.revokeObjectURL(signaturePreview);
    }
    setUploadedSignature(null);
    setSignaturePreview(null);
  };

  const generatePDF = async (): Promise<Blob | null> => {
    if (!slaContentRef.current) return null;

    try {
      // Capture the SLA content as canvas
      const canvas = await html2canvas(slaContentRef.current, {
        scale: 1.5, // Reduced from 2 to save size
        useCORS: true,
        logging: false,
      });

      const imgData = canvas.toDataURL("image/jpeg", 0.75); // Using JPEG with compression quality 0.75
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      const imgWidth = 210; // A4 width in mm
      const pageHeight = 297; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      // Add first page
      pdf.addImage(
        imgData,
        "JPEG",
        0,
        position,
        imgWidth,
        imgHeight,
        undefined,
        "FAST",
      );
      heightLeft -= pageHeight;

      // Add additional pages if content is longer than one page
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(
          imgData,
          "JPEG",
          0,
          position,
          imgWidth,
          imgHeight,
          undefined,
          "FAST",
        );
        heightLeft -= pageHeight;
      }

      return pdf.output("blob");
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error("Failed to generate PDF document");
      return null;
    }
  };

  const handlePreviewSLA = async () => {
    if (!agreed) {
      toast.error("Please agree to the terms to continue.");
      return;
    }

    if (!uploadedSignature) {
      toast.error("Please upload your signature before submitting.");
      return;
    }

    if (!id) {
      toast.error("Organization ID is missing.");
      return;
    }

    // Open modal immediately to show loading state
    setPreviewModal(true);
    setPdfBlobUrl(null);
    setPdfFile(null);

    try {
      const pdfBlob = await generatePDF();
      if (!pdfBlob) {
        setPreviewModal(false);
        return;
      }

      const file = new File(
        [pdfBlob],
        `SLA_${clientName}_${format(new Date(), "yyyy-MM-dd")}.pdf`,
        { type: "application/pdf" },
      );

      setPdfFile(file);
      setPdfBlobUrl(URL.createObjectURL(pdfBlob));
    } catch (error) {
      console.error("Error generating PDF preview:", error);
      toast.error("Failed to generate PDF preview.");
      setPreviewModal(false);
    }
  };

  const handleSubmitSLA = async () => {
    if (!pdfFile || !id) return;

    setIsSubmitting(true);
    try {
      // 1. Upload signed PDF
      await documentService.uploadDocument(id, "SLA", pdfFile);

      // 2. Extract and send order data via SLA email service
      const tier = searchParams.get("tier") || "";
      const frequency = searchParams.get("frequency") || "MONTHLY";
      const featureIds = searchParams.get("featureIds")?.split(",") || [];
      const directPlan = searchParams.get("directPlan");
      const directFeatureIds =
        searchParams.get("directAddOnIds")?.split(",") || [];
      const lifecyclePhaseIds =
        searchParams.get("lifecyclePhaseIds")?.split(",").filter(Boolean) || [];
      const amount = searchParams.get("amount")
        ? Number(searchParams.get("amount"))
        : undefined;

      const slaData: any = {
        tier,
        organizationId: id,
        frequency,
        featureIds,
        amount,
        lifecyclePhaseIds,
      };

      if (directPlan) {
        slaData.directPlan = {
          tier: directPlan,
          includeSetupFee: searchParams.get("includeSetupFee") === "true",
        };
      }

      if (directFeatureIds.length > 0) {
        slaData.directFeatureIds = directFeatureIds;
      }

      // await organizationService.sendSlaEmail(id, slaData);

      setPreviewModal(false);
      if (pdfBlobUrl) URL.revokeObjectURL(pdfBlobUrl);
      setPdfBlobUrl(null);
      setPdfFile(null);

      setStatusModal({
        isOpen: true,
        status: "success",
        message: "SLA Document submitted!",
      });
    } catch (error: any) {
      console.error("Error submitting SLA:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to submit SLA document";
      setStatusModal({
        isOpen: true,
        status: "error",
        message: errorMessage,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClosePreview = () => {
    if (pdfBlobUrl) URL.revokeObjectURL(pdfBlobUrl);
    setPdfBlobUrl(null);
    setPdfFile(null);
    setPreviewModal(false);
  };

  const handleAgree = () => {
    if (!agreed) {
      toast.error("Please agree to the terms to continue.");
      return;
    }

    // Forward all params to payment selection
    // navigate({
    //   pathname: "/payment-selection",
    //   search: searchParams.toString(),
    // });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        Loading...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-[1024px] mx-auto">
        <h1 className="text-3xl font-bold text-center mb-4 text-gray-800">
          Review Service Level Agreement
        </h1>

        <div ref={slaContentRef}>
          <SlaContent
            clientName={clientName}
            representativeName={representativeName}
            representativeRole={representativeRole}
            date={format(new Date(), "do MMMM, yyyy")}
            uploadedSignature={uploadedSignature}
            signaturePreview={signaturePreview}
            onFileUpload={handleFileUpload}
            onRemoveSignature={handleRemoveSignature}
          />
        </div>

        <div className="mt-4 bg-white p-4 rounded-lg shadow-sm border border-gray-100 sticky bottom-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="agree-checkbox"
              className="w-5 h-5 cursor-pointer accent-primary"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
            />
            <label
              htmlFor="agree-checkbox"
              className="text-gray-700 cursor-pointer select-none text-sm"
            >
              I have read and agree to the Service Level Agreement{" "}
              {clientName !== "[Client]" ? `for ${clientName}` : ""}
            </label>
          </div>

          <div className="flex justify-end gap-4">
            {/* <button
              onClick={() => navigate(-1)}
              className="px-6 py-2 border border-gray-300 rounded-md hover:bg-gray-50 text-gray-700 font-medium transition-colors"
            >
              Back
            </button> */}
            <Button2
              title="Preview SLA"
              onSubmitHandler={handlePreviewSLA}
              className={`px-8 py-2 rounded-md text-white font-medium transition-colors ${
                agreed && uploadedSignature
                  ? "bg-primary hover:bg-primary/90"
                  : "bg-gray-400 cursor-not-allowed"
              }`}
              disabled={!agreed || !uploadedSignature}
            />
            {/* <Button2
              title={isSubmitting ? "Processing..." : "Agree & Continue"}
              onSubmitHandler={handleAgree}
              className={`px-8 py-2 rounded-md text-white font-medium transition-colors ${
                agreed && !isSubmitting
                  ? "bg-primary hover:bg-primary/90"
                  : "bg-gray-400 cursor-not-allowed"
              }`}
              disabled={!agreed || isSubmitting}
            /> */}
          </div>
        </div>
      </div>

      <PdfPreviewModal
        isOpen={previewModal}
        onClose={handleClosePreview}
        pdfUrl={pdfBlobUrl}
        onSubmit={handleSubmitSLA}
        isSubmitting={isSubmitting}
        title="SLA Document Preview"
        submitButtonText="Submit SLA"
      />

      <StatusModal
        isOpen={statusModal.isOpen}
        status={statusModal.status}
        message={statusModal.message}
        onClose={() => setStatusModal((prev) => ({ ...prev, isOpen: false }))}
        onAction={
          statusModal.status === "success"
            ? () => {
                navigate({
                  pathname: `/data-processing`,
                  search: `Id=${id}`,
                });
              }
            : undefined
        }
      />
    </div>
  );
};

export default SlaPageAgreement;
